import { useMemo } from "react";
import { Activity } from "lucide-react";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import {
  BrushStart,
  ChartType,
  LegendKey,
} from "../../../../models/chart_model";
import { GRANULARITY, Granularity, SupplyPoint } from "../types";

export function SupplyChart({
  series,
  granularity,
  setGranularity,
  receiver,
  title,
}: {
  series: SupplyPoint[];
  granularity: Granularity;
  setGranularity: (g: Granularity) => void;
  receiver?: string;
  title?: string;
}) {
  const legends: LegendKey[] = useMemo(
    () => [
      {
        dataKey: "actual",
        name: "Actual",
        type: ChartType.Bar,
        stroke: "#F1935C",
        unit: "MW",
        yAxisId: "left",
      },
      {
        dataKey: "scheduled",
        name: "Scheduled",
        type: ChartType.Line,
        stroke: "#2E90FA",
        unit: "MW",
        yAxisId: "left",
      },
    ],
    [],
  );

  return (
    <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-3.5 mb-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-[#525062]">
            {title ??
              (receiver
                ? `Supply To ${receiver}`
                : "Supply: actual vs scheduled")}
          </div>
        </div>
        <div className="flex items-center gap-3.5">
          <div className="inline-flex bg-[#F2F3F7] rounded-lg p-0.5 gap-0.5 ml-2">
            {GRANULARITY.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className={`bg-transparent border-none px-2.5 py-1 text-[11px] rounded-md cursor-pointer ${
                  granularity === g
                    ? "bg-white text-[#525062] font-semibold shadow-[0_1px_2px_rgba(20,22,36,0.06)]"
                    : "text-[#828A99]"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2">
        {series.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[280px] text-center px-6 bg-[#FDFCFA] rounded-lg border border-dashed border-[#EBE5DA]">
            <div className="w-12 h-12 rounded-full bg-[#FDF1E8] flex items-center justify-center mb-3">
              <Activity size={22} className="text-[#E2682B]" />
            </div>
            <div className="text-[13px] font-semibold text-[#525062]">
              No banking activity yet
            </div>
            <div className="text-[12px] text-[#828A99] mt-1 leading-relaxed max-w-[280px]">
              Scheduled and actual flows for this contract will appear here once
              the {granularity.toLowerCase()} data is available.
            </div>
          </div>
        ) : (
          <ReMixChart
            data={series as any}
            legends={legends}
            xDataKey="date"
            secondXDataKey={
              granularity === "Time Block" ? "secondaryDate" : undefined
            }
            isTimeSlot={granularity === "Time Block"}
            yAxisLabel="MW"
            unit="MW"
            showBrush={series.length > 96}
            brushStart={BrushStart.End}
            onlyTitle={true}
            barCategoryGap={10}
            fontSize={11}
            top={10}
            bottom={granularity === "Time Block" ? 40 : 20}
            chartContainerHeight="280px"
          />
        )}
      </div>
    </div>
  );
}

export default SupplyChart;
